import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPANIONS_NAMESPACE } from '@/i18n';
import type { UseCompanionsResult } from '@/Pages/Companions/hooks/useCompanions';
import { applyCpfMask } from '@/Pages/Signup/utils/cpfMask';

type Props = { companions: UseCompanionsResult };

/**
 * O MESMO formulário para cadastrar e para editar — são os dados que a própria
 * pessoa preencheria no `/signup` (nome, nascimento, CPF, e-mail,
 * nacionalidade). Editando, o bloco da foto some: a foto tem caminho próprio na
 * linha da lista, e misturar os dois fazia o titular achar que precisava
 * reenviar a foto para corrigir um nome.
 */
export function CompanionForm({ companions }: Props): JSX.Element {
  const { t } = useTranslation(COMPANIONS_NAMESPACE);
  const captureInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const {
    fullName,
    birthDate,
    document,
    email,
    nationality,
    editingId,
    editingDocumentMasked,
    hasPhoto,
    consentConfirmed,
    canSelfOnboard,
    photoError,
    submitError,
    isSubmitting,
    canSubmitWithPhoto,
    canSubmitForSelfOnboarding,
    setFullName,
    setBirthDate,
    setDocument,
    setEmail,
    setNationality,
    setConsentConfirmed,
    pickPhoto,
    submitWithPhoto,
    saveEdit,
    cancelEdit,
  } = companions;

  const isEditing = editingId !== null;
  const canSave = fullName.trim().length >= 2 && !isSubmitting;

  return (
    <Box
      component="form"
      data-testid="companion-form"
      onSubmit={(event) => {
        event.preventDefault();
        void (isEditing ? saveEdit() : submitWithPhoto());
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Divider />
      <Typography component="h3" variant="subtitle1">
        {isEditing ? t('editTitle') : t('formTitle')}
      </Typography>

      <TextField
        label={t('nameLabel')}
        placeholder={t('namePlaceholder')}
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        inputProps={{ 'data-testid': 'companion-full-name' }}
        fullWidth
      />
      <TextField
        label={t('birthDateLabel')}
        type="date"
        value={birthDate}
        onChange={(event) => setBirthDate(event.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'data-testid': 'companion-birth-date' }}
        helperText={t('birthDateHelp')}
        fullWidth
      />
      <TextField
        label={t('documentLabel')}
        placeholder={t('documentPlaceholder')}
        value={applyCpfMask(document)}
        onChange={(event) => setDocument(event.target.value)}
        inputProps={{ inputMode: 'numeric', 'data-testid': 'companion-document' }}
        // Editando, o CPF gravado só existe mascarado: digitar troca, vazio mantém.
        helperText={
          isEditing && editingDocumentMasked !== null
            ? t('documentKeepHelp', { masked: editingDocumentMasked })
            : t('documentHelp')
        }
        fullWidth
      />
      <TextField
        label={t('emailLabel')}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        inputProps={{ 'data-testid': 'companion-email' }}
        helperText={t('emailHelp')}
        fullWidth
      />
      <TextField
        label={t('nationalityLabel')}
        placeholder={t('nationalityPlaceholder')}
        value={nationality}
        onChange={(event) => setNationality(event.target.value)}
        inputProps={{ 'data-testid': 'companion-nationality' }}
        fullWidth
      />

      {submitError !== null && (
        <Alert severity="error" data-testid="companion-submit-error">
          {submitError}
        </Alert>
      )}

      {isEditing ? (
        <>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!canSave}
            data-testid="companion-save"
          >
            {isSubmitting ? t('submitLoading') : t('saveCta')}
          </Button>
          <Button variant="text" onClick={cancelEdit} data-testid="companion-cancel-edit">
            {t('cancelEditCta')}
          </Button>
        </>
      ) : (
        <>
          {/* Câmera (capture) e galeria/arquivo — dois inputs escondidos. */}
          <input
            ref={captureInputRef}
            type="file"
            accept="image/*"
            capture="user"
            hidden
            data-testid="companion-capture-input"
            onChange={(event) => pickPhoto(event.target.files?.[0] ?? null)}
          />
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            hidden
            data-testid="companion-upload-input"
            onChange={(event) => pickPhoto(event.target.files?.[0] ?? null)}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" fullWidth onClick={() => captureInputRef.current?.click()}>
              {t('captureButton')}
            </Button>
            <Button variant="outlined" fullWidth onClick={() => uploadInputRef.current?.click()}>
              {t('uploadButton')}
            </Button>
          </Box>

          {hasPhoto && <Alert severity="success">{t('photoReady')}</Alert>}
          {photoError === 'too_large' && <Alert severity="error">{t('photoTooLarge')}</Alert>}
          {photoError === 'invalid_type' && <Alert severity="error">{t('photoInvalidType')}</Alert>}

          <FormControlLabel
            control={
              <Checkbox
                checked={consentConfirmed}
                onChange={(event) => setConsentConfirmed(event.target.checked)}
                data-testid="companion-consent"
              />
            }
            label={t('consentLabel')}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!canSubmitWithPhoto || isSubmitting}
            data-testid="companion-submit"
          >
            {isSubmitting ? t('submitLoading') : t('submit')}
          </Button>

          {/* Acima de 16 anos o adicional pode fazer o próprio cadastro: com
              CPF ele se identifica na URL do evento; sem CPF sai o link. */}
          {canSelfOnboard && (
            <>
              <Divider>{t('orDivider')}</Divider>
              <Button
                variant="outlined"
                size="large"
                disabled={!canSubmitForSelfOnboarding || isSubmitting}
                onClick={() => void companions.submitForSelfOnboarding()}
                data-testid="companion-self-onboarding"
              >
                {t('selfOnboardingCta')}
              </Button>
              <Typography component="p" variant="caption" color="text.secondary">
                {document.length >= 11
                  ? t('selfOnboardingByDocumentHint')
                  : t('selfOnboardingByLinkHint')}
              </Typography>
            </>
          )}
          {birthDate !== '' && !canSelfOnboard && (
            <Typography
              component="p"
              variant="caption"
              color="text.secondary"
              data-testid="companion-minor-hint"
            >
              {t('minorHint')}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
